import { Anchor, AppShell, Burger, Group, NavLink, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCreditCard,
  IconDoorExit,
  IconFileText,
  IconListCheck,
  IconLock,
  IconUsers,
  IconChartBar,
} from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useSession } from '../../lib/session';

const nav = [
  { to: '/eltern', label: 'Überblick', icon: IconChartBar },
  { to: '/eltern/kinder', label: 'Kinder', icon: IconUsers },
  { to: '/eltern/lehrplan', label: 'Lehrplan', icon: IconListCheck },
  { to: '/eltern/arbeitsblaetter', label: 'Arbeitsblätter', icon: IconFileText },
  { to: '/eltern/abo', label: 'Abo', icon: IconCreditCard },
  { to: '/eltern/sicherheit', label: 'Sicherheit', icon: IconLock },
];

/** The parent's side of the app. Deliberately plain: this is the half nobody
 * uses for fun, and it should be readable rather than delightful. */
export function DashboardLayout() {
  const [opened, { toggle }] = useDisclosure();
  const { pairing, leave, refreshChildren } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  // The roster was stored when the device was paired. A grade counts up on
  // 1 August on the server alone, so the parent's side reads it fresh once
  // each time it opens. Once: refreshChildren changes with what it loads.
  const refreshed = useRef(false);
  useEffect(() => {
    if (refreshed.current) return;
    refreshed.current = true;
    void refreshChildren();
  }, [refreshChildren]);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 220, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={5}>{pairing?.orgName ?? 'Sokrates'}</Title>
          </Group>
          <NavLink
            component="button"
            label="Verlassen"
            leftSection={<IconDoorExit size={18} />}
            w="auto"
            onClick={() => {
              leave();
              navigate('/');
            }}
          />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            component={Link}
            to={item.to}
            label={item.label}
            leftSection={<item.icon size={18} />}
            active={
              item.to === '/eltern'
                ? location.pathname === '/eltern'
                : location.pathname.startsWith(item.to)
            }
          />
        ))}
        <Text size="xs" c="dimmed" mt="md" px="xs">
          Sokrates gibt keine Lösungen. Er stellt Fragen.
        </Text>
        <Anchor component={Link} to="/datenschutz" size="xs" c="dimmed" mt="xs" px="xs">
          Datenschutz
        </Anchor>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
